import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useMemo, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';

import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Card,
  Alert,
  TextField,
  CardHeader,
  CardActions,
  CardContent,
  MenuItem
} from '@mui/material';

import { useCustomFields } from 'src/hooks/use-custom-fields';

import { useTranslate } from 'src/locales';
import { OrganizationCandidateType } from 'src/services/candidates/candidates.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { generateCandidateUpdateErrorMessage } from 'src/services/candidates/candidates.utils';
import { useGetOrganizationCandidateQuery, useSetCandidateProfileMutation, useUpdateUnlinkedCandidateMutation } from 'src/services/candidates/candidates.service';

import Iconify from 'src/components/iconify';
import CommunicationActions from 'src/sections/admin/integrations/communications/comms-actions';
import { buildDialNumber } from 'src/sections/admin/integrations/communications/use-communication-actions';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { TITLE_OPTIONS } from 'src/utils/title-options';

type Props = {
  candidateId: string;
};

export const CandidateContactCard = ({ candidateId }: Props) => {
  const { t } = useTranslate();

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.CANDIDATE,
    entityId: candidateId
  });

  const { currentData: orgCandidate, isLoading } = useGetOrganizationCandidateQuery(
    candidateId,
    {
      skip: !candidateId,
    }
  );

  const [updateCandidate, { isLoading: isUpdatingCandidate }] = useUpdateUnlinkedCandidateMutation();
  const [setCandidateProfile] = useSetCandidateProfileMutation();

  const UpdateCompanyProfileSchema = Yup.object().shape({
    title: Yup.string().nullable(),
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    email: Yup.string().email(t('validation.email_format')).nullable().optional(),

    phone_number: Yup.string().nullable(),
    phone_number_validation: Yup.string().nullable(),
    country_code: Yup.string().nullable(),
    location: Yup.string().nullable(),
    position_title: Yup.string().nullable(),
    special_information: Yup.string().nullable(),
  });

  const defaultValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    phone_number_validation: '',
    country_code: '',
    location: '',
    position_title: '',
    special_information: '',
  };

  const methods = useForm({
    resolver: yupResolver(UpdateCompanyProfileSchema),
    defaultValues,
  });

  const {
    setValue,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = methods;

  useEffect(() => {
    if (!orgCandidate) return;

    if (orgCandidate) {
      setValue(
        'title',
        orgCandidate.candidate_profile.title ?? orgCandidate.synced_profile?.title ?? ''
      );
      setValue('first_name', orgCandidate.candidate_profile.first_name ?? '');
      setValue('last_name', orgCandidate.candidate_profile.last_name ?? '');
      setValue('position_title', orgCandidate.candidate_profile.position_title ?? '');

      setValue('email', orgCandidate.email ?? '');
      setValue('phone_number', orgCandidate.phone_number ?? '');
      setValue('country_code', orgCandidate.country_code ?? '');
      setValue('location', orgCandidate.candidate_profile?.location ?? '');
      setValue('special_information', orgCandidate.special_information ?? '');
    }

  }, [orgCandidate, setValue]);

  const handleDetailUpdate = handleSubmit(async (data: any) => {
    if (!orgCandidate) return;

    // Validate custom fields first
    const customFieldValidation = customFields.validate();
    if (!customFieldValidation.isValid) {
      return;
    }

    try {
      await updateCandidate({
        candidateId: orgCandidate.id,
        data: {
          title: data.title || undefined,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email || undefined,
          phone_number: data.phone_number,
          country_code: data.country_code,
          position_title: data.position_title,
          special_information: data.special_information,
          custom_fields: customFields.getData()
        },
      }).unwrap();

      await setCandidateProfile({
        id: orgCandidate.candidate_profile.id!,
        data: { location: data.location || undefined },
      }).unwrap();

      enqueueSnackbar(t('candidates.api.update_unlinked_candidate.success'), {
        variant: 'success',
      });
    }
    catch (error) {
      const errMsg = generateCandidateUpdateErrorMessage(error);
      enqueueSnackbar(errMsg, { variant: 'error' });
    }
  });

  const detailsEditable = useMemo(() => {
    if (!orgCandidate) return false;

    return [OrganizationCandidateType.UNLINKED].includes(orgCandidate.type);
  }, [orgCandidate]);

  if (isLoading) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
            <Iconify icon="solar:user-id-broken" />
            <Box>{t('candidates.detail.details.title')}</Box>
          </Stack>
        }
        action={
          orgCandidate?.privileged_access ? (
            <CommunicationActions
              phoneNumber={buildDialNumber(orgCandidate?.phone_number, orgCandidate?.country_code)}
              name={`${orgCandidate?.candidate_profile?.first_name ?? ''} ${orgCandidate?.candidate_profile?.last_name ?? ''}`.trim()}
              entity={orgCandidate ? { entity_type: CommunicationEntityType.CANDIDATE, entity_id: orgCandidate.id } : undefined}
            />
          ) : undefined
        }
      />
      <CardContent sx={{ mt: -2 }}>
        <FormProvider methods={methods}>
          <Grid container spacing={2}>
            <Grid xs={12}>
              {
                !detailsEditable && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="info" icon={false} variant='outlined'>
                      {t('candidates.detail.details.editable_info_text')}
                    </Alert>
                  </Box>
                )
              }
            </Grid>
            <Grid xs={12} sm={12}>
              <RHFSelect
                name="title"
                disabled={!detailsEditable}
                label={t('common.form_labels.title')}
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {TITLE_OPTIONS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`enums.title_options.${value}`)}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>
            <Grid xs={6} sm={6}>
              <RHFTextField
                name="first_name"
                disabled={!detailsEditable}
                label={t('common.form_labels.first_name')}
                placeholder={t('common.placeholders.first_name')}
              />
            </Grid>

            <Grid xs={6} sm={6}>
              <RHFTextField
                name="last_name"
                disabled={!detailsEditable}
                label={t('common.form_labels.last_name')}
                placeholder={t('common.placeholders.last_name')}
              />
            </Grid>

            <Grid xs={12} sm={12}>
              {
                orgCandidate?.privileged_access && (
                  <RHFTextField
                    name="email"
                    disabled={!detailsEditable}
                    label={t('common.form_labels.email')}
                    placeholder={t('common.placeholders.email')}
                  />
                )
              }

              {
                !orgCandidate?.privileged_access && (
                  <TextField
                    fullWidth
                    label={t('common.form_labels.email')}
                    placeholder={t('candidates.detail.details.privileged_info_text')}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <Iconify
                          sx={{ mr: 1 }}
                          icon="uil:lock"
                        />
                      )
                    }}
                  />
                )
              }
            </Grid>

            <Grid xs={12} sm={12}>
              {
                orgCandidate?.privileged_access && (
                  <RHFTextField
                    name="phone_number"
                    disabled={!detailsEditable}
                    label={t('account.profile.form.phone.label')}
                    placeholder={t('account.profile.form.phone.placeholder')}
                  />
                )
              }

              {
                !orgCandidate?.privileged_access && (
                  <TextField
                    fullWidth
                    label={t('account.profile.form.phone.label')}
                    placeholder={t('candidates.detail.details.privileged_info_text')}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <Iconify
                          sx={{ mr: 1 }}
                          icon="uil:lock"
                        />
                      )
                    }}
                  />
                )
              }
            </Grid>

            <Grid xs={12} sm={12}>
              <RHFTextField
                name="location"
                disabled={!detailsEditable}
                label={t('common.form_labels.location')}
                placeholder={t('common.placeholders.location')}
              />
            </Grid>

            <Grid xs={12} sm={12}>
              <RHFTextField
                name="position_title"
                disabled={!detailsEditable}
                label={t('common.form_labels.position_title')}
                placeholder={t('common.placeholders.position_title')}
              />
            </Grid>

            <Grid xs={12} sm={12}>
              <RHFTextField
                name="special_information"
                label={t('candidates.editor-sidesheet.form.special_information.label')}
                placeholder={t('candidates.editor-sidesheet.form.special_information.placeholder')}
                multiline
                minRows={2}
                maxRows={10}
              />
            </Grid>

            {/* Custom Fields */}
            {customFields.hasCustomFields && (
              <>
                {customFields.renderCustomFields}
              </>
            )}
          </Grid>
        </FormProvider>
      </CardContent>

      <CardActions>
        <Stack direction="row" justifyContent="end" spacing={1} sx={{ p: 2, width: '100%' }}>
          <LoadingButton
            variant="outlined"
            color="inherit"
            loading={isSubmitting || isUpdatingCandidate}
            onClick={handleDetailUpdate}
          >
            {t('common.update')}
          </LoadingButton>
        </Stack>
      </CardActions>
    </Card>
  );
};